/**
 * Product Intelligence Tracker
 * Tabbed dashboard for tracking bugs, improvements, and marketing strategies
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrackChanges as TrackChangesIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Campaign as CampaignIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { colors, spacing } from '../../theme/designTokens';
import productIntelligenceService, {
  ProductIntelligenceItem,
  DevNote,
  Attachment,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  CreateItemPayload,
} from '../../services/productIntelligenceService';

// TabPanel helper (same pattern as UserManagement.tsx)
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pi-tabpanel-${index}`}
      aria-labelledby={`pi-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

// Category mapping
const CATEGORIES = ['bug', 'improvement', 'marketing'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  bug: 'Bugs',
  improvement: 'Improvements',
  marketing: 'Marketing Ideas',
};

// Priority chip colors
const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (priority) {
    case 'P0': return 'error';
    case 'P1': return 'warning';
    case 'P2': return 'info';
    case 'P3': return 'default';
    default: return 'default';
  }
};

// Status chip color
const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'default' => {
  switch (status) {
    case 'Open':
    case 'Backlog':
    case 'Idea':
      return 'warning';
    case 'In Progress':
    case 'Scoped':
      return 'info';
    case 'Resolved':
    case 'Active':
    case 'Deployed - Passed Testing':
      return 'success';
    case 'Deployed - Needs Testing':
      return 'warning';
    case 'Paused':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusVariant = (status: string): 'filled' | 'outlined' => {
  return status === 'Deployed - Passed Testing' ? 'outlined' : 'filled';
};

export const ProductIntelligence: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [items, setItems] = useState<ProductIntelligenceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Expanded cards
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Add note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogItemId, setNoteDialogItemId] = useState('');
  const [noteLabel, setNoteLabel] = useState('');
  const [noteValue, setNoteValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Create item dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createStatus, setCreateStatus] = useState('');
  const [createPriority, setCreatePriority] = useState('');
  const [createSubcategory, setCreateSubcategory] = useState('');
  const [createReporter, setCreateReporter] = useState('Brandon Steiner');
  const [createAssignee, setCreateAssignee] = useState('');
  const [createTargetDate, setCreateTargetDate] = useState('');

  // Attachments
  const [attachments, setAttachments] = useState<Record<string, Attachment[]>>({});
  const [uploadingAttachment, setUploadingAttachment] = useState<string | null>(null);
  const [attachUploadItemId, setAttachUploadItemId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const currentCategory = CATEGORIES[tabValue];

  const fetchItems = useCallback(async () => {
    try {
      setError(null);
      const filters: { status?: string; priority?: string; search?: string } = {};
      if (statusFilter) filters.status = statusFilter;
      if (priorityFilter) filters.priority = priorityFilter;
      if (searchQuery) filters.search = searchQuery;

      const result = await productIntelligenceService.getItems(currentCategory, filters);
      setItems(result.items);
    } catch (err: any) {
      console.error('Failed to fetch items:', err);
      setError('Failed to load items. Backend may not be running.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentCategory, statusFilter, priorityFilter, searchQuery]);

  // Fetch + 60-second polling
  useEffect(() => {
    setLoading(true);
    fetchItems();
    const interval = setInterval(fetchItems, 60000);
    return () => clearInterval(interval);
  }, [fetchItems]);

  // Reset filters when switching tabs
  useEffect(() => {
    setStatusFilter('');
    setPriorityFilter('');
    setSearchQuery('');
  }, [tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStatusChange = async (itemId: string, newStatus: string) => {
    try {
      await productIntelligenceService.updateItem(itemId, { status: newStatus });
      fetchItems();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleAddNote = async () => {
    if (!noteLabel.trim() || !noteValue.trim()) return;
    setSubmitting(true);
    try {
      await productIntelligenceService.updateItem(noteDialogItemId, {
        add_note: { label: noteLabel.trim(), value: noteValue.trim() },
      });
      setNoteDialogOpen(false);
      setNoteLabel('');
      setNoteValue('');
      setNoteDialogItemId('');
      fetchItems();
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateTitle('');
    setCreateDescription('');
    setCreateStatus(STATUS_OPTIONS[currentCategory]?.[0] || '');
    setCreatePriority('');
    setCreateSubcategory('');
    setCreateReporter('Brandon Steiner');
    setCreateAssignee('');
    setCreateTargetDate('');
    setCreateDialogOpen(true);
  };

  const handleCreateItem = async () => {
    if (!createTitle.trim() || !createStatus) return;
    setCreating(true);
    try {
      const payload: CreateItemPayload = {
        category: currentCategory,
        title: createTitle.trim(),
        status: createStatus,
      };
      if (createDescription.trim()) payload.description = createDescription.trim();
      if (createPriority) payload.priority = createPriority;
      if (createSubcategory.trim()) payload.subcategory = createSubcategory.trim();
      if (createReporter.trim()) payload.reporter = createReporter.trim();
      if (createAssignee.trim()) payload.assignee = createAssignee.trim();
      if (createTargetDate) payload.target_date = createTargetDate;

      await productIntelligenceService.createItem(payload);
      setCreateDialogOpen(false);
      fetchItems();
    } catch (err) {
      console.error('Failed to create item:', err);
    } finally {
      setCreating(false);
    }
  };

  const toggleExpand = (itemId: string) => {
    const willExpand = !expandedItems[itemId];
    setExpandedItems(prev => ({ ...prev, [itemId]: willExpand }));
    // Lazy-load attachments on first expand
    if (willExpand && !attachments[itemId]) {
      productIntelligenceService.getAttachments(itemId).then(data => {
        setAttachments(prev => ({ ...prev, [itemId]: data }));
      }).catch(err => console.error('Failed to load attachments:', err));
    }
  };

  const handleAttachClick = (itemId: string) => {
    setAttachUploadItemId(itemId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !attachUploadItemId) return;

    // Client-side 10MB validation
    if (file.size > 10 * 1024 * 1024) {
      alert('File must be under 10MB');
      e.target.value = '';
      return;
    }

    setUploadingAttachment(attachUploadItemId);
    try {
      const newAttachment = await productIntelligenceService.uploadAttachment(attachUploadItemId, file);
      setAttachments(prev => ({
        ...prev,
        [attachUploadItemId]: [newAttachment, ...(prev[attachUploadItemId] || [])],
      }));
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setUploadingAttachment(null);
      setAttachUploadItemId(null);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (itemId: string, attachmentId: number) => {
    try {
      await productIntelligenceService.deleteAttachment(itemId, attachmentId);
      setAttachments(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).filter(a => a.id !== attachmentId),
      }));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPriorityFilter('');
  };

  // Metric counts
  const totalItems = items.length;
  const inProgressCount = items.filter(i => i.status === 'In Progress').length;
  const p0Count = items.filter(i => i.priority === 'P0').length;
  const deployedCount = items.filter(i => i.status === 'Deployed - Passed Testing' || i.status === 'Deployed - Needs Testing' || i.status === 'Active').length;

  // Check if note label suggests a testing checklist
  const isTestingNote = (label: string) => /testing|checklist/i.test(label);

  const tabSx = {
    minHeight: 64,
    fontSize: '1rem',
    fontWeight: 600,
    '& .MuiTab-iconWrapper': {
      marginRight: spacing[2],
      marginBottom: 0,
    },
    '&.Mui-selected': {
      color: colors.deepTeal[500],
    },
  };

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: spacing[3],
            mb: spacing[2],
          }}
        >
          <TrackChangesIcon color="primary" sx={{ fontSize: 40 }} />
          Product Intelligence
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
          Track bugs, feature improvements, and marketing strategies
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: colors.deepTeal[500], mb: spacing[6] }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.deepTeal[500],
              height: 3,
            },
          }}
        >
          <Tab label="Bugs" icon={<BugReportIcon />} iconPosition="start" sx={tabSx} />
          <Tab label="Improvements" icon={<LightbulbIcon />} iconPosition="start" sx={tabSx} />
          <Tab label="Marketing Ideas" icon={<CampaignIcon />} iconPosition="start" sx={tabSx} />
        </Tabs>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: spacing[3], mb: spacing[5], flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {(STATUS_OPTIONS[currentCategory] || []).map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {currentCategory !== 'marketing' && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e: SelectChangeEvent) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {(searchQuery || statusFilter || priorityFilter) && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            Clear
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{
            backgroundColor: colors.deepTeal[500],
            '&:hover': { backgroundColor: colors.deepTeal[600] },
          }}
        >
          Add {currentCategory === 'bug' ? 'Bug' : currentCategory === 'improvement' ? 'Improvement' : 'Marketing'}
        </Button>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: spacing[6] }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: spacing[3] }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.deepTeal[500] }}>
              {totalItems}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total {CATEGORY_LABELS[currentCategory]}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: spacing[3] }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.info.main }}>
              {inProgressCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">In Progress</Typography>
          </Card>
        </Grid>
        {currentCategory !== 'marketing' && (
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: spacing[3] }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: colors.error.main }}>
                {p0Count}
              </Typography>
              <Typography variant="body2" color="text.secondary">P0 Critical</Typography>
            </Card>
          </Grid>
        )}
        <Grid item xs={6} sm={3}>
          <Card sx={{ textAlign: 'center', p: spacing[3] }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.success.main }}>
              {deployedCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentCategory === 'marketing' ? 'Active' : 'Deployed'}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Error state */}
      {error && (
        <Alert severity="warning" sx={{ mb: spacing[4] }}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: spacing[8] }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tab Panels */}
      {!loading && (
        <>
          <TabPanel value={tabValue} index={0}>
            <ItemList
              items={items}
              category="bug"
              expandedItems={expandedItems}
              onToggleExpand={toggleExpand}
              onStatusChange={handleStatusChange}
              onAddNote={(itemId) => { setNoteDialogItemId(itemId); setNoteDialogOpen(true); }}
              isTestingNote={isTestingNote}
              attachments={attachments}
              uploadingAttachment={uploadingAttachment}
              onAttachFile={handleAttachClick}
              onDeleteAttachment={handleDeleteAttachment}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ItemList
              items={items}
              category="improvement"
              expandedItems={expandedItems}
              onToggleExpand={toggleExpand}
              onStatusChange={handleStatusChange}
              onAddNote={(itemId) => { setNoteDialogItemId(itemId); setNoteDialogOpen(true); }}
              isTestingNote={isTestingNote}
              attachments={attachments}
              uploadingAttachment={uploadingAttachment}
              onAttachFile={handleAttachClick}
              onDeleteAttachment={handleDeleteAttachment}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ItemList
              items={items}
              category="marketing"
              expandedItems={expandedItems}
              onToggleExpand={toggleExpand}
              onStatusChange={handleStatusChange}
              onAddNote={(itemId) => { setNoteDialogItemId(itemId); setNoteDialogOpen(true); }}
              isTestingNote={isTestingNote}
              attachments={attachments}
              uploadingAttachment={uploadingAttachment}
              onAttachFile={handleAttachClick}
              onDeleteAttachment={handleDeleteAttachment}
            />
          </TabPanel>
        </>
      )}

      {/* Empty state */}
      {!loading && !error && items.length === 0 && (
        <Box sx={{ textAlign: 'center', py: spacing[10] }}>
          <Typography variant="h6" color="text.secondary">
            No {CATEGORY_LABELS[currentCategory].toLowerCase()} found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery || statusFilter || priorityFilter
              ? 'Try adjusting your filters'
              : 'Items will appear here once added'}
          </Typography>
        </Box>
      )}

      {/* Add Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Dev Note to {noteDialogItemId}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Label"
            fullWidth
            value={noteLabel}
            onChange={(e) => setNoteLabel(e.target.value)}
            placeholder="e.g. Root Cause, Testing Checklist, Status Update"
            sx={{ mb: spacing[3] }}
          />
          <TextField
            margin="dense"
            label="Note Content"
            fullWidth
            multiline
            rows={4}
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value)}
            placeholder="Enter note details..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddNote}
            variant="contained"
            disabled={submitting || !noteLabel.trim() || !noteValue.trim()}
            sx={{ backgroundColor: colors.deepTeal[500], '&:hover': { backgroundColor: colors.deepTeal[600] } }}
          >
            {submitting ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input for attachments */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
        onChange={handleFileSelected}
      />

      {/* Create Item Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New {currentCategory === 'bug' ? 'Bug' : currentCategory === 'improvement' ? 'Improvement' : 'Marketing Item'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            required
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            sx={{ mb: spacing[3] }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            sx={{ mb: spacing[3] }}
          />
          <Box sx={{ display: 'flex', gap: spacing[3], mb: spacing[3] }}>
            <FormControl size="small" sx={{ flex: 1 }} required>
              <InputLabel>Status</InputLabel>
              <Select
                value={createStatus}
                label="Status"
                onChange={(e: SelectChangeEvent) => setCreateStatus(e.target.value)}
              >
                {(STATUS_OPTIONS[currentCategory] || []).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentCategory !== 'marketing' && (
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={createPriority}
                  label="Priority"
                  onChange={(e: SelectChangeEvent) => setCreatePriority(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {PRIORITY_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <TextField
            margin="dense"
            label="Subcategory"
            fullWidth
            size="small"
            value={createSubcategory}
            onChange={(e) => setCreateSubcategory(e.target.value)}
            sx={{ mb: spacing[3] }}
          />
          <Box sx={{ display: 'flex', gap: spacing[3], mb: spacing[3] }}>
            <TextField
              margin="dense"
              label="Reporter"
              size="small"
              value={createReporter}
              onChange={(e) => setCreateReporter(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              margin="dense"
              label="Assignee"
              size="small"
              value={createAssignee}
              onChange={(e) => setCreateAssignee(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            margin="dense"
            label="Target Date"
            type="date"
            size="small"
            fullWidth
            value={createTargetDate}
            onChange={(e) => setCreateTargetDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateItem}
            variant="contained"
            disabled={creating || !createTitle.trim() || !createStatus}
            sx={{ backgroundColor: colors.deepTeal[500], '&:hover': { backgroundColor: colors.deepTeal[600] } }}
          >
            {creating ? 'Creating...' : 'Create Item'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Item List subcomponent
interface ItemListProps {
  items: ProductIntelligenceItem[];
  category: string;
  expandedItems: Record<string, boolean>;
  onToggleExpand: (id: string) => void;
  onStatusChange: (id: string, status: string) => void;
  onAddNote: (id: string) => void;
  isTestingNote: (label: string) => boolean;
  attachments: Record<string, Attachment[]>;
  uploadingAttachment: string | null;
  onAttachFile: (id: string) => void;
  onDeleteAttachment: (itemId: string, attachmentId: number) => void;
}

const ItemList: React.FC<ItemListProps> = ({
  items,
  category,
  expandedItems,
  onToggleExpand,
  onStatusChange,
  onAddNote,
  isTestingNote,
  attachments,
  uploadingAttachment,
  onAttachFile,
  onDeleteAttachment,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
      {items.map((item) => {
        const isExpanded = expandedItems[item.item_id] || false;

        return (
          <Card
            key={item.item_id}
            sx={{
              cursor: 'pointer',
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: 4 },
            }}
          >
            <CardContent sx={{ pb: isExpanded ? 0 : undefined }}>
              {/* Card Header */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: spacing[2], flexWrap: 'wrap' }}
                onClick={() => onToggleExpand(item.item_id)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: colors.deepTeal[500],
                    minWidth: 100,
                  }}
                >
                  {item.item_id}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  {item.title}
                </Typography>
                <Chip
                  label={item.status}
                  color={getStatusColor(item.status)}
                  variant={getStatusVariant(item.status)}
                  size="small"
                />
                {item.priority && (
                  <Chip
                    label={item.priority}
                    color={getPriorityColor(item.priority)}
                    size="small"
                    variant="outlined"
                  />
                )}
                <IconButton size="small">
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {/* Expandable Detail Section */}
              <Collapse in={isExpanded}>
                <Box sx={{ mt: spacing[4], pt: spacing[3], borderTop: '1px solid', borderColor: 'divider' }}>
                  {/* Description */}
                  {item.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: spacing[3] }}>
                      {item.description}
                    </Typography>
                  )}

                  {/* Metadata Row */}
                  <Box sx={{ display: 'flex', gap: spacing[4], flexWrap: 'wrap', mb: spacing[4] }}>
                    {item.reporter && (
                      <Typography variant="body2">
                        <strong>Reporter:</strong> {item.reporter}
                      </Typography>
                    )}
                    {item.assignee && (
                      <Typography variant="body2">
                        <strong>Assignee:</strong> {item.assignee}
                      </Typography>
                    )}
                    {item.subcategory && (
                      <Typography variant="body2">
                        <strong>Subcategory:</strong> {item.subcategory}
                      </Typography>
                    )}
                    {item.target_date && (
                      <Typography variant="body2">
                        <strong>Target:</strong> {new Date(item.target_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>

                  {/* Inline Status Change */}
                  <Box sx={{ display: 'flex', gap: spacing[3], alignItems: 'center', mb: spacing[4] }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={item.status}
                        label="Status"
                        onChange={(e: SelectChangeEvent) => {
                          e.stopPropagation();
                          onStatusChange(item.item_id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(STATUS_OPTIONS[category] || []).map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={(e) => { e.stopPropagation(); onAddNote(item.item_id); }}
                      sx={{ color: colors.deepTeal[500] }}
                    >
                      Add Note
                    </Button>
                    <Button
                      size="small"
                      startIcon={uploadingAttachment === item.item_id ? <CircularProgress size={16} /> : <AttachFileIcon />}
                      onClick={(e) => { e.stopPropagation(); onAttachFile(item.item_id); }}
                      disabled={uploadingAttachment === item.item_id}
                      sx={{ color: colors.deepTeal[500] }}
                    >
                      {uploadingAttachment === item.item_id ? 'Uploading...' : 'Attach File'}
                    </Button>
                  </Box>

                  {/* Dev Notes */}
                  {item.dev_notes && item.dev_notes.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[2], mb: spacing[3] }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: spacing[1] }}>
                        Dev Notes
                      </Typography>
                      {item.dev_notes.map((note: DevNote, idx: number) => (
                        <Box
                          key={idx}
                          sx={{
                            borderLeft: `4px solid ${isTestingNote(note.label) ? '#D4AC0D' : colors.deepTeal[500]}`,
                            pl: spacing[3],
                            py: spacing[2],
                            bgcolor: 'action.hover',
                            borderRadius: '0 4px 4px 0',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            {note.label}
                          </Typography>
                          <Typography
                            component="pre"
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              m: 0,
                              fontSize: '0.8125rem',
                            }}
                          >
                            {note.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Attachments */}
                  {attachments[item.item_id] && attachments[item.item_id].length > 0 && (
                    <Box sx={{ mb: spacing[3] }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: spacing[2] }}>
                        Attachments
                      </Typography>
                      <Box sx={{ display: 'flex', gap: spacing[3], flexWrap: 'wrap' }}>
                        {attachments[item.item_id].map((att) => {
                          const isImage = att.mime_type.startsWith('image/');
                          return (
                            <Box
                              key={att.id}
                              sx={{
                                position: 'relative',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1,
                                textAlign: 'center',
                                maxWidth: 140,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onDeleteAttachment(item.item_id, att.id); }}
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  width: 24,
                                  height: 24,
                                  '&:hover': { bgcolor: 'error.light', color: 'white' },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              {isImage ? (
                                <Box
                                  component="a"
                                  href={att.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  sx={{ display: 'block', cursor: 'pointer' }}
                                >
                                  <Box
                                    component="img"
                                    src={att.download_url}
                                    alt={att.original_name}
                                    sx={{
                                      width: 120,
                                      height: 90,
                                      objectFit: 'cover',
                                      borderRadius: 0.5,
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  component="a"
                                  href={att.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  sx={{ display: 'block', textDecoration: 'none', color: 'inherit', cursor: 'pointer', py: 1 }}
                                >
                                  <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                                </Box>
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 120,
                                }}
                                title={att.original_name}
                              >
                                {att.original_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {att.file_size < 1024
                                  ? `${att.file_size} B`
                                  : `${Math.round(att.file_size / 1024)} KB`}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Updated timestamp */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: spacing[2], mb: spacing[2] }}>
                    Last updated: {new Date(item.updated_at).toLocaleString()}
                  </Typography>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default ProductIntelligence;
